import { FC, useContext, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import { SearchContextProvider, useSearchContext } from "../../context/searchContext";
import useI18n from "../../hooks/useTranslations";
import { Project as FullProject, UpdateProjectBody } from "../../http/types/projects";
import { useProjectsApi } from "../../http/useProjects";
import ProjectConfigurations from "../configurations/ProjectConfigurations";
import SvgFactCheck from "../icons/FactCheck";
import SvgModule from "../icons/Module";
import SvgTextSnippet from "../icons/TextSnippet";
import Header from "../ui/Header";
import Tabs, { Tab } from "../ui/Tabs";
import ProjectDetails, { ProjectDetailsFormBody } from "./ProjectDetails";
import ProjectDocuments from "./ProjectDocuments";
import ProjectSharingAccessesDialog from "./ProjectSharingAccessesDialog";

interface ProjectProps {
  isAdmin?: boolean;
}

const Project: FC<ProjectProps> = ({ isAdmin }: ProjectProps) => {
  return (
    <SearchContextProvider>
      <ProjectContent isAdmin={isAdmin} />
    </SearchContextProvider>
  );
};

const ProjectContent: FC<ProjectProps> = ({ isAdmin }: ProjectProps) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const projectsApi = useProjectsApi();
  const [isSharingDialogOpen, setSharingDialogOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const { actions, title: tabTitle } = useSearchContext();

  const { projectId } = useParams<{ projectId: string }>();
  const selectedIndex = Number(searchParams.get("tab")) || 0;

  const aborter = useRef(new AbortController());

  const { error, data, trigger } = isAdmin
    ? projectsApi.getAdminProject(aborter.current.signal)
    : projectsApi.getProject(aborter.current.signal);

  useEffect(() => {
    projectId && trigger(projectId);
  }, [projectId]);

  const submit = async (body: ProjectDetailsFormBody) => {
    if (data?.id) {
      const updateBody: UpdateProjectBody = {
        name: body.name!,
        address: body.address,
        city: body.city,
        country: body.country,
        coordinates: body.coordinates,
        description: body.description,
        language: body.language?.value!,
        status: body.status?.value!,
        company: body.company,
        contactPerson: body.contactPerson,
      };

      const optimisticData: FullProject = {
        ...data,
        ...updateBody,
      };

      try {
        return await projectsApi.updateProject(data.id, updateBody, optimisticData, isAdmin);
      } catch (err) {
        return dispatch(showErrorNotification(i18n, err));
      }
    }
  };

  const tabs: Tab[] = [
    { title: i18n.translation.common.details, glyph: SvgFactCheck },
    { title: i18n.translation.configurations.plural, glyph: SvgModule },
    { title: i18n.translation.documents.plural, glyph: SvgTextSnippet },
  ];

  return (
    <>
      <Header
        title={tabTitle ? `${i18n.translation.projects.singular} · ${tabTitle}` : i18n.translation.projects.singular}
        error={error}
        path={[
          {
            display: isAdmin ? i18n.translation.projects.all : i18n.translation.projects.my,
            url: `${isAdmin ? "/admin" : ""}/projects`,
          },
          { display: data?.name! },
        ]}
        buttons={actions}
      />
      <Tabs
        urlParameter="tab"
        tabs={tabs}
        hideBorderBottom
        contents={[
          <ProjectDetails
            project={data}
            onSubmit={submit}
            disabled={!!error}
            isLoading={!data && !error}
            isAdmin={isAdmin}
            canEdit={data?.canEdit}
          />,
          <ProjectConfigurations projectId={projectId!} isAdmin={isAdmin} disabled={!!error} canEdit={data?.canEdit} />,
          <ProjectDocuments isAdmin={isAdmin} canEdit={data?.canEdit} active={selectedIndex === 2} />,
        ]}
      />
      {isSharingDialogOpen && projectId && (
        <ProjectSharingAccessesDialog
          projectId={projectId}
          projectName={data?.name}
          isAdmin={isAdmin}
          onClose={() => setSharingDialogOpen(false)}
        />
      )}
    </>
  );
};

export default Project;
